import { ETextStyleVariant, Text } from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React from 'react';
import { useIntl } from 'react-intl';

import { PriceType } from '@/redux/selectors/listing/page';
import { formatCurrency } from '@/utility/currency';

import { PriceSize } from '../Price';
import css from './PriceCurrent.module.css';

type ICurrentPriceElement = React.HTMLAttributes<HTMLElement>;

interface IProps {
  currency: string;
  currentPrice: number;
  originalPrice: number;
  period: PriceType;
  size?: PriceSize;
  testId?: string;
  isCampsite?: boolean;
  isRVBundle?: boolean;
  duration?: number;
  totalPrice?: number;
  isMapPopup?: boolean;
  originalTotalPrice?: number;
  showDiscount?: boolean;
  hasDiscount?: boolean;
  hidePeriod?: boolean;
  /** Extra classes for the "for N nights/days" duration suffix (all-in-total layout). */
  periodClassName?: string;
}

const priceTemplate = (value: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.price}>
    {value}
  </span>
);

const periodTemplate = (period: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.period}>
    {period}
  </span>
);

const discountPriceTemplate = (value: React.ReactNode): React.ReactElement => (
  <span aria-hidden className={css.originalTotalPrice}>
    {value}
  </span>
);

const PriceCurrent: React.FC<IProps & ICurrentPriceElement> = ({
  size = PriceSize.medium,
  currency,
  currentPrice,
  originalPrice,
  period,
  testId = 'price-current',
  isCampsite = false,
  isRVBundle = false,
  duration,
  totalPrice,
  isMapPopup,
  originalTotalPrice,
  showDiscount,
  hasDiscount,
  hidePeriod,
  periodClassName,
}) => {
  const intl = useIntl();

  // Format the appropriate price based on the context
  const formattedCurrentPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: totalPrice ? totalPrice : currentPrice,
    ...(isRVBundle && { ceil: true }),
  });

  const formattedOriginalPrice = formatCurrency({
    currency,
    digits: 0,
    maxDigits: 0,
    priceInCents: originalTotalPrice ? originalTotalPrice : originalPrice,
    ...(isRVBundle && { ceil: true }),
  });

  const screenReaderMessage = intl.formatMessage(
    {
      description: '[Price]: screen reader message for current price per period (day/night)',
      defaultMessage: '{currentPrice} per {period, select, day {day} other {night}}',
      id: 'fodvXy',
    },
    {
      currentPrice: formattedCurrentPrice,
      period,
    },
  );

  const isDay = period === PriceType.DAY;

  const renderPrice = () => {
    if (hidePeriod) {
      return priceTemplate(
        isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
      );
    }

    if (typeof duration === 'number' && duration > 0) {
      if (isMapPopup) {
        if (showDiscount && hasDiscount) {
          if (isDay) {
            return intl.formatMessage(
              {
                description: '[Price]: current price formatting for a day-priced listing',
                defaultMessage:
                  '<discountPriceTemplate>{originalTotalPrice}</discountPriceTemplate><priceTemplate>{value}</priceTemplate><periodTemplate>for {days, plural, one {# day} other {# days}}</periodTemplate>',
                id: 'LtH5qv',
              },
              {
                discountPriceTemplate: discountPriceTemplate,
                priceTemplate: priceTemplate,
                periodTemplate: isCampsite ? priceTemplate : periodTemplate,
                days: duration,
                value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
                originalTotalPrice: isRVBundle
                  ? `RV + Site ${formattedOriginalPrice}`
                  : formattedOriginalPrice,
              },
            );
          }
          return intl.formatMessage(
            {
              description: '[Price]: current price formatting for a listing',
              defaultMessage:
                '<discountPriceTemplate>{originalTotalPrice}</discountPriceTemplate><priceTemplate>{value}</priceTemplate><periodTemplate>for {nights, plural, one {# night} other {# nights}}</periodTemplate>',
              id: '+X3Ex0',
            },
            {
              discountPriceTemplate: discountPriceTemplate,
              priceTemplate: priceTemplate,
              periodTemplate: isCampsite ? priceTemplate : periodTemplate,
              nights: duration,
              value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
              originalTotalPrice: isRVBundle
                ? `RV + Site ${formattedOriginalPrice}`
                : formattedOriginalPrice,
            },
          );
        }

        if (isDay) {
          return intl.formatMessage(
            {
              description: '[Price]: current price formatting for a day-priced listing',
              defaultMessage:
                '<priceTemplate>{value}</priceTemplate><periodTemplate>for {days, plural, one {# day} other {# days}}</periodTemplate>',
              id: 'x1kbse',
            },
            {
              priceTemplate: priceTemplate,
              periodTemplate: isCampsite ? priceTemplate : periodTemplate,
              days: duration,
              value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
            },
          );
        }
        return intl.formatMessage(
          {
            description: '[Price]: current price formatting for a listing',
            defaultMessage:
              '<priceTemplate>{value}</priceTemplate><periodTemplate>for {nights, plural, one {# night} other {# nights}}</periodTemplate>',
            id: 'aIZeRu',
          },
          {
            priceTemplate: priceTemplate,
            periodTemplate: isCampsite ? priceTemplate : periodTemplate,
            nights: duration,
            value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
          },
        );
      }

      return (
        <div className="flex order-1 truncate gap-1">
          {showDiscount && hasDiscount && (
            <Text
              aria-hidden
              variant={ETextStyleVariant.LargeBold}
              className="line-through text-[#909398]">
              {isRVBundle ? `RV + Site ${formattedOriginalPrice}` : formattedOriginalPrice}
            </Text>
          )}
          <Text aria-hidden variant={ETextStyleVariant.LargeBold}>
            {isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice}
          </Text>
          <Text
            aria-hidden
            variant={ETextStyleVariant.LargeRegular}
            className={clsx('truncate', periodClassName)}>
            {isDay
              ? intl.formatMessage(
                  {
                    defaultMessage: 'for {days, plural, one {# day} other {# days}}',
                    description: '[Price]: total price duration for a day-priced listing',
                    id: '/DiqrB',
                  },
                  { days: duration },
                )
              : intl.formatMessage(
                  {
                    defaultMessage: 'for {nights, plural, one {# night} other {# nights}}',
                    id: 'jzRwv5',
                    description: '[Price]: total price formatting for a listing',
                  },
                  { nights: duration },
                )}
          </Text>
        </div>
      );
    }

    if (showDiscount && hasDiscount) {
      return intl.formatMessage(
        {
          description: '[Price]: current price formatting for a listing',
          defaultMessage:
            '<discountPriceTemplate>{originalTotalPrice}</discountPriceTemplate><priceTemplate>{value}</priceTemplate><periodTemplate>/{period, select, day {day} night {night} other {}}</periodTemplate>',
          id: '3Na735',
        },
        {
          discountPriceTemplate: discountPriceTemplate,
          priceTemplate: priceTemplate,
          periodTemplate: isCampsite ? priceTemplate : periodTemplate,
          period: period?.toLowerCase(),
          value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
          originalTotalPrice: isRVBundle
            ? `RV + Site ${formattedOriginalPrice}`
            : formattedOriginalPrice,
        },
      );
    }

    return intl.formatMessage(
      {
        description: '[Price]: current price formatting for a listing',
        defaultMessage:
          '<priceTemplate>{value}</priceTemplate><periodTemplate>/{period, select, day {day} night {night} other {}}</periodTemplate>',
        id: 'eJ7KW9',
      },
      {
        priceTemplate: priceTemplate,
        periodTemplate: isCampsite ? priceTemplate : periodTemplate,
        period: period?.toLowerCase(),
        value: isRVBundle ? `RV + Site ${formattedCurrentPrice}` : formattedCurrentPrice,
      },
    );
  };

  return (
    <p
      className={css.container}
      aria-label={screenReaderMessage}
      data-testid={testId}
      data-size={size}>
      {renderPrice()}
    </p>
  );
};

export default PriceCurrent;
